import { logger } from 'monitor';
import { createContext, useEffect, useState } from 'react';

export type NetworkStatusModes = 'auto' | 'offline';

interface NetworkStatus {
  isOnline: boolean;
  mode: NetworkStatusModes;
  setMode: (mode: NetworkStatusModes) => void;
  setStatus: (isOnline: boolean) => void;
  isConnectedToInternet: boolean;
}

export const NetworkStatusContext = createContext<NetworkStatus | null>(null);

export const NetworkStatusProvider = ({ children }: { children: React.ReactNode }) => {
  const isNavigatorExists = typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'; // navigator may not be available in all environments, so we need to check if it's available before using it
  const [isOnline, setIsOnline] = useState(isNavigatorExists ? navigator.onLine : true);
  const isConnectedToInternet = isNavigatorExists ? navigator.onLine : true;
  const [mode, setMode] = useState<'auto' | 'offline'>('auto');

  useEffect(() => {
    if (mode === 'offline') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOnline(false);
      return;
    }

    if (mode === 'auto') {
      const handleOnline = () => {
        logger.info('NetworkStatusContext: Online event detected, setting isOnline to true');
        setIsOnline(true);
      };

      const handleOffline = () => {
        logger.info('NetworkStatusContext: Offline event detected, setting isOnline to false');
        setIsOnline(false);
      };

      setIsOnline(isNavigatorExists ? navigator.onLine : true);

      if (isNavigatorExists) {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
        };
      }
    }
  }, [mode, isNavigatorExists]);

  const value = {
    isConnectedToInternet,
    isOnline,
    mode,
    setMode,
    setStatus: setIsOnline,
  };

  return <NetworkStatusContext.Provider value={value}>{children}</NetworkStatusContext.Provider>;
};
